import { ReactComponent as CloseIcon } from '@assets/svg/icon-menu-close.svg';
import { ReactComponent as HamburgerIcon } from '@assets/svg/icon-menu-hamburger.svg';
import { ReactComponent as LogoutIconDark } from '@assets/svg/icon-menu-logout-dark.svg';
import Avatar from '@components/avatar';
import Button from '@components/button';
import LangSwitcher from '@components/lang-switcher';
import Portal from '@components/ui/portal';
import { useAppSelector } from '@hooks/app/redux';
import useTokenService from '@hooks/app/useTokenService';
import { useMediumQuery } from '@lib/hooks/useMediumQuery';
import { STEPS } from '@pages/auth/registration/registration.types';
import { TokenService } from '@services/internal';
import { animate, motion, useMotionValue, useScroll, useTransform } from 'framer-motion';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import AnimatedHilbiLogo from './components/animated-hilbi-logo';
import AnimatedLogoutIcon from './components/animated-logout-icon';
import { MobileMenuSC, NavUnauthSC } from './nav-unauth.styled';

const SCROLL_ANIM_HEIGHT = 60;

const NavUnauth: React.FunctionComponent = () => {
  const containerRef = React.useRef<HTMLElement>(null);
  const currentStep = useAppSelector((state) => state.auth.currentRegStep);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const { scrollY } = useScroll();
  const _menuScrollPos = useMotionValue(0);
  const scrollValue = menuOpen ? _menuScrollPos : scrollY;

  const scrollHeight = Math.abs(window.innerHeight - document.body.clientHeight);
  const maxRange =
    scrollHeight === 0 ? SCROLL_ANIM_HEIGHT : Math.min(scrollHeight, SCROLL_ANIM_HEIGHT);

  const _logoColor = useTransform(scrollValue, [0, maxRange], ['#ffffff', '#333D6C'], {
    clamp: true,
  });
  const _textColor = useTransform(
    scrollValue,
    [0, maxRange],
    [currentStep === STEPS.ROLE ? '#333D6C' : '#ffffff', '#333D6C'],
    { clamp: true },
  );
  const _backColor = useTransform(
    scrollValue,
    [0, maxRange],
    ['rgba(255,255,255,0.15)', 'rgba(255,255,255,1)'],
    { clamp: true },
  );

  const isNarrowScreen = useMediumQuery('(max-width: 900px)');
  const { session } = useTokenService();

  /**
   * Toggle mobile menu, and change colors if needed
   */
  const toggleMenu = React.useCallback((open?: boolean) => {
    setMenuOpen((o) => {
      const next = open !== undefined ? open : !o;

      if (next) {
        const hasVScroll = window.innerWidth > document.documentElement.clientWidth;
        const bodyClass = hasVScroll ? 'auth-menu-open-overflown' : 'auth-menu-open';
        document.body.classList.add(bodyClass);

        animate(_menuScrollPos, SCROLL_ANIM_HEIGHT, {
          duration: 0.3,
        });
      } else {
        document.body.classList.remove('auth-menu-open');
        document.body.classList.remove('auth-menu-open-overflown');

        animate(_menuScrollPos, scrollY.get(), {
          duration: 0.3,
        });
      }

      return next;
    });
  }, []);

  const logOut = React.useCallback(() => {
    TokenService.revokeSession();
  }, []);

  /**
   * Close menu, when is not mobile
   */
  React.useEffect(() => {
    if (!isNarrowScreen) {
      toggleMenu(false);
    }
  }, [isNarrowScreen]);

  /**
   * Close menu, when leaving
   */
  React.useEffect(() => {
    return () => {
      toggleMenu(false);
    };
  }, []);

  const userName = session
    ? `${session.decodedUserToken.firstName} ${session.decodedUserToken.lastName}`
    : null;

  return (
    <>
      <NavUnauthSC ref={containerRef} style={{ background: _backColor }}>
        <Link className="logo" to="/login">
          <AnimatedHilbiLogo color={_logoColor} />
        </Link>
        <div className="nav-right">
          <LangSwitcher theme="authorized" className="lang-switcher" />
          {isNarrowScreen && userName ? (
            <button className="hamburger" onClick={() => toggleMenu()}>
              {menuOpen ? <CloseIcon width={20} height={20} /> : <HamburgerIcon />}
            </button>
          ) : null}
          {!isNarrowScreen && userName ? (
            <motion.div className="user" style={{ color: _textColor }}>
              <motion.div style={{ borderColor: _logoColor }} className="separator" />
              {userName}
              <button className="logout-btn" onClick={logOut}>
                <AnimatedLogoutIcon color={_textColor} />
              </button>
            </motion.div>
          ) : null}
        </div>
      </NavUnauthSC>
      <Portal>
        <CSSTransition in={menuOpen} unmountOnExit timeout={300} classNames="auth-menu">
          <MobileMenuSC>
            <div className="content">
              <Avatar size={64} />
              <div className="user-name">{userName}</div>
              <div className="user-email">{session?.decodedUserToken.email}</div>
            </div>
            <Button onClick={logOut}>
              <LogoutIconDark style={{ marginRight: 8 }} />{' '}
              <FormattedMessage id="user_drop.logout" />
            </Button>
          </MobileMenuSC>
        </CSSTransition>
      </Portal>
    </>
  );
};

export default NavUnauth;
